import { useState, useEffect } from "react";
import { economyApi } from "../api/economy";
import { useGameStore } from "../store/useGameStore";
import type { ShopItem } from "../types";
import clsx from "clsx";
import { getConsumableIconPath } from "../utils/assets";
import { FormattedNumber } from "../components/FormattedNumber";

const QTY_PRESETS = [1, 5, 10] as const;
const MAX_QTY = 99;
const VIP_SHOP_ITEM_ID = "vip_subscription";

function maxAffordable(item: ShopItem, gold: number, diamonds: number): number {
  const limits = [MAX_QTY];
  if (item.cost_gold > 0) limits.push(Math.floor(gold / item.cost_gold));
  if (item.cost_diamonds > 0) limits.push(Math.floor(diamonds / item.cost_diamonds));
  return Math.max(0, Math.min(...limits));
}

export function ShopScreen() {
  const [items, setItems] = useState<ShopItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const { balance, refreshBalance, refreshHero } = useGameStore();

  useEffect(() => {
    economyApi
      .getShop()
      .then(setItems)
      .finally(() => setLoading(false));
  }, []);

  const getQty = (itemId: string) => quantities[itemId] ?? 1;

  const setQty = (itemId: string, next: number, item: ShopItem) => {
    const cap = maxAffordable(item, balance?.gold ?? 0, balance?.diamonds ?? 0);
    const clamped = Math.max(1, Math.min(MAX_QTY, cap || 1, next));
    setQuantities((prev) => ({ ...prev, [itemId]: clamped }));
  };

  const handleBuy = async (item: ShopItem) => {
    const quantity = item.item_id === VIP_SHOP_ITEM_ID ? 1 : getQty(item.item_id);
    setBuying(item.item_id);
    setMessage(null);
    try {
      const res = await economyApi.buyItem(item.item_id, quantity);
      setMessage(
        quantity > 1
          ? `✅ Purchased ×${quantity}! Gold: ${res.remaining_gold.toLocaleString("en-US")}`
          : `✅ Purchased! Gold: ${res.remaining_gold.toLocaleString("en-US")}`,
      );
      await refreshBalance();
      if (item.item_id === VIP_SHOP_ITEM_ID) {
        await refreshHero();
        setMessage("✅ VIP subscription activated or extended!");
      }
    } catch (e) {
      setMessage(`❌ ${e instanceof Error ? e.message : "Purchase failed"}`);
    } finally {
      setBuying(null);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <h2 className="text-xl font-bold font-game text-center text-gold-400">🛒 Shop</h2>

      {message && (
        <div
          className={clsx(
            "rounded-xl p-3 text-sm border",
            message.startsWith("✅")
              ? "bg-emerald-900/30 border-emerald-500/50 text-emerald-300"
              : "bg-rose-900/30 border-rose-500/50 text-rose-300",
          )}
        >
          {message}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading shop...</div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const isVip = item.item_id === VIP_SHOP_ITEM_ID;
            const quantity = isVip ? 1 : getQty(item.item_id);
            const totalGold = item.cost_gold * quantity;
            const totalDiamonds = item.cost_diamonds * quantity;
            const canAffordGold = !totalGold || (balance?.gold ?? 0) >= totalGold;
            const canAffordDiamonds = !totalDiamonds || (balance?.diamonds ?? 0) >= totalDiamonds;
            const canAfford = canAffordGold && canAffordDiamonds;
            const affordableMax = maxAffordable(item, balance?.gold ?? 0, balance?.diamonds ?? 0);

            return (
              <div
                key={item.item_id}
                className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 flex items-center gap-3"
              >
                {isVip ? (
                  <div className="w-12 h-12 rounded-lg border border-amber-500/50 bg-amber-900/40 flex items-center justify-center text-2xl shrink-0">
                    👑
                  </div>
                ) : (
                  <img
                    src={getConsumableIconPath(item.item_id)}
                    alt={item.name}
                    className="w-12 h-12 rounded-lg border border-kingdom-mid bg-white object-contain shrink-0"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = "none";
                    }}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-white">{item.name}</div>
                  <div className="text-gray-400 text-xs mt-0.5">{item.description}</div>
                  <div className="flex gap-3 mt-2 flex-wrap">
                    {item.cost_gold > 0 && (
                      <span className={clsx("text-sm font-bold", canAffordGold ? "text-gold-400" : "text-gray-600")}>
                        🪙 <FormattedNumber value={quantity > 1 ? totalGold : item.cost_gold} />
                        {quantity > 1 && (
                          <span className="text-gray-500 font-normal text-xs ml-1">
                            ({quantity}× <FormattedNumber value={item.cost_gold} />)
                          </span>
                        )}
                      </span>
                    )}
                    {item.cost_diamonds > 0 && (
                      <span
                        className={clsx("text-sm font-bold", canAffordDiamonds ? "text-purple-400" : "text-gray-600")}
                      >
                        💎 <FormattedNumber value={quantity > 1 ? totalDiamonds : item.cost_diamonds} />
                        {quantity > 1 && (
                          <span className="text-gray-500 font-normal text-xs ml-1">
                            ({quantity}× <FormattedNumber value={item.cost_diamonds} />)
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                  {!isVip && (
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setQty(item.item_id, quantity - 1, item)}
                      disabled={quantity <= 1}
                      className="w-7 h-7 rounded-lg border border-kingdom-mid text-white disabled:opacity-40"
                    >
                      −
                    </button>
                    <span className="text-sm text-white font-bold min-w-[2rem] text-center">{quantity}</span>
                    <button
                      type="button"
                      onClick={() => setQty(item.item_id, quantity + 1, item)}
                      disabled={quantity >= affordableMax || quantity >= MAX_QTY}
                      className="w-7 h-7 rounded-lg border border-kingdom-mid text-white disabled:opacity-40"
                    >
                      +
                    </button>
                    {QTY_PRESETS.map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => setQty(item.item_id, preset, item)}
                        disabled={preset > affordableMax}
                        className={clsx(
                          "text-xs px-2 py-1 rounded-lg border",
                          quantity === preset
                            ? "border-kingdom-glow text-kingdom-glow bg-kingdom-mid/50"
                            : "border-kingdom-mid text-gray-400 disabled:opacity-40",
                        )}
                      >
                        ×{preset}
                      </button>
                    ))}
                    {affordableMax > 10 && (
                      <button
                        type="button"
                        onClick={() => setQty(item.item_id, affordableMax, item)}
                        className="text-xs px-2 py-1 rounded-lg border border-kingdom-mid text-gray-400"
                      >
                        Max
                      </button>
                    )}
                  </div>
                  )}
                </div>
                <button
                  onClick={() => void handleBuy(item)}
                  disabled={!canAfford || buying === item.item_id}
                  className="px-4 py-2 bg-gradient-to-r from-kingdom-accent to-purple-700 text-white text-sm font-bold rounded-lg active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap self-end"
                >
                  {buying === item.item_id ? "..." : quantity > 1 ? `Buy ×${quantity}` : "Buy"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
